import { NgStyle } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  model,
  OnDestroy,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { ContextService } from '@services/context';
import { LogsService } from '@services/logs';
import { getCDNLink } from '@shared/components/image/utils';
import { pauseStyle, playStyle } from '@shared/components/video/config';
import { VideoStyle } from '@shared/components/video/model';
import { VideoService } from '@shared/components/video/services';
import { IconsDirective, InsideViewportDirective } from '@shared/directives';
import { Image } from '@shared/models';
import { MediaUrlPipe } from '@shared/pipes';
import { MediaUrlParams } from '@shared/pipes/model';
import { getId, getStableId } from '@shared/utils';

@Component({
  selector: 'app-video',
  imports: [IconsDirective, InsideViewportDirective, NgStyle, MediaUrlPipe],
  providers: [MediaUrlPipe],
  templateUrl: './video.component.html',
  styleUrl: './video.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.cover]': 'cover()',
  },
})
export class VideoComponent implements OnDestroy {
  readonly videoElement = viewChild.required<ElementRef<HTMLVideoElement>>('video');
  readonly mp4Url = input<string>();
  readonly webmUrl = input<string>();
  readonly previewImage = input<Image>();
  readonly style = input<VideoStyle>({});
  readonly isPlayFullscreen = input(false);
  readonly isResetAfterFullscreen = input(false);
  readonly isMuted = input(false);
  readonly isLoop = input(false);
  readonly posterStyle = model<MediaUrlParams>();
  readonly isHidePosterGoingFullscreen = input(false);
  readonly autoplay = input(false);
  readonly cover = input(false);
  readonly videoStart = output<string>();
  readonly videoComplete = output<string>();
  readonly idVideo = `video-${getId()}`;
  video!: HTMLVideoElement;
  videoObserver!: ResizeObserver;
  controlsStyle = signal<Record<string, string>>({
    visibility: 'hidden',
  });
  url!: string;
  isPlayButtonDisplayed = signal(true);
  isSoundOnOffDisplayed = signal(false);
  iconPlaySize = signal<string>(null!);
  iconPauseSize = signal<string>(null!);
  iconSoundSize = signal<string>(null!);
  isVideoDisplayed = signal(false);
  buttonPauseStyle = signal({});
  buttonSoundStyle = signal({});
  isSoundOn = signal(true);
  protected readonly mp4UrlCdn = computed(() => getCDNLink(this.mp4Url()));
  protected readonly webmUrlCdn = computed(() => getCDNLink(this.webmUrl()));
  /** Stable ID for tracking this video in the queue */
  protected readonly videoId = computed(() => getStableId(this.mp4Url() ?? this.webmUrl() ?? this.idVideo));
  /** Indicates if the video source was cleared to release GPU resources */
  protected readonly isLobotomized = signal(false);
  /** Effective video URLs (empty when lobotomized) */
  protected readonly effectiveMp4Url = computed(() => (this.isLobotomized() ? '' : (this.mp4UrlCdn() ?? '')));
  protected readonly effectiveWebmUrl = computed(() => (this.isLobotomized() ? '' : (this.webmUrlCdn() ?? '')));
  protected readonly posterLink = computed(() => {
    const img = this.previewImage();
    return img !== null && typeof img === 'object' ? img.link : '';
  });
  /** Tracks if the video is currently playing via the queue */
  protected readonly isPlaying = signal(false);
  /** Tracks if autoplay failed due to browser policy */
  protected readonly isAutoplayError = signal(false);
  /** Tracks if the video can play (metadata loaded) */
  protected readonly isCanPlay = signal(false);
  /** Muted state that can be controlled internally for autoplay */
  protected readonly isMutedInternal = linkedSignal(() => this.isMuted());
  /** Loop state that can be controlled internally for autoplay */
  protected readonly isLoopInternal = linkedSignal(() => this.isLoop());
  readonly #contextService = inject(ContextService);
  readonly #mediaPipe = inject(MediaUrlPipe);
  readonly #window = inject(DOCUMENT).defaultView as Window;
  readonly #videoService = inject(VideoService);
  readonly #logsService = inject(LogsService);
  readonly #activeVideo = this.#videoService.activeVideo;

  constructor() {
    afterNextRender(() => {
      this.getElements();
      this.setStyle();
      if (this.#contextService.isBrowser()) {
        this.observeSize();
      }
      this.observeVideoEnd();
    });

    // Effect to coordinate playback with the VideoService queue
    effect(() => {
      const isCanPlay = this.isCanPlay();
      const activeVideo = this.#activeVideo();

      if (!isCanPlay) return;

      const id = untracked(this.videoId);
      const isActive = activeVideo === id;
      const isCurrentlyPlaying = untracked(this.isPlaying);

      if (!isActive && isCurrentlyPlaying) {
        this.#stopAutoplay();
      } else if (isActive && !isCurrentlyPlaying) {
        this.#playAutoplay();
      }
    });
  }

  onPlay() {
    void this.video.play().catch((error: unknown) => {
      this.#logsService.warn('VideoComponent', 'Manual play failed', error);
    });
    this.videoStart.emit('Video start');
    if (!this.video.classList.contains('playing')) this.video.classList.add('playing');
    if (this.isPlayFullscreen()) {
      // requestFullscreen is unavailable on iPhone Safari: calling it unguarded throws a sync TypeError
      if (typeof this.video.requestFullscreen === 'function') {
        this.video
          .requestFullscreen()
          .catch((error: unknown) => this.#logsService.warn('VideoComponent', 'Fullscreen request rejected', error));
      }
      if (this.isHidePosterGoingFullscreen()) this.video.poster = '';
    }
    if (!this.isResetAfterFullscreen()) {
      this.isPlayButtonDisplayed.set(false);
      this.isSoundOnOffDisplayed.set(true);
      this.controlsStyle.set({
        ...this.controlsStyle(),
        ...pauseStyle,
      });
    }
  }

  onPause() {
    void this.video.pause();
    this.resetPlayButton();
  }

  onFullScreenChange(): void {
    if (!document.fullscreenElement && this.isResetAfterFullscreen()) {
      const previewImage = this.previewImage();
      if (this.isHidePosterGoingFullscreen() && previewImage?.link)
        this.video.poster = this.#mediaPipe.transform(previewImage.link, this.posterStyle());
      this.video.load();
      if (this.video.classList.contains('playing')) {
        this.video.classList.remove('playing');
      }
    }
  }

  ngOnDestroy(): void {
    try {
      this.#videoService.removeVideoFromQueue(this.videoId());
      if (this.videoObserver) {
        this.videoObserver.unobserve(this.video);
      }
      if (this.video) {
        this.video.removeEventListener('ended', this.#videoEnded);
        this.video.removeEventListener('fullscreenchange', this.onFullScreenChange.bind(this));
      }
    } catch (error) {
      this.#logsService.error('VideoComponent', 'Error during ngOnDestroy:', error);
    }
  }

  resetPlayButton = (): void => {
    this.isPlayButtonDisplayed.set(true);
    this.isSoundOnOffDisplayed.set(false);
    this.controlsStyle.set({
      ...this.controlsStyle(),
      ...playStyle,
    });
  };

  onToggleSound(): void {
    this.isSoundOn.set(!this.isSoundOn());
    this.video.muted = !this.isSoundOn();
  }

  private getElements(): void {
    this.video = this.videoElement().nativeElement;
    this.video.disablePictureInPicture = true;
    this.video.disableRemotePlayback = true;
    this.video.addEventListener('fullscreenchange', this.onFullScreenChange.bind(this));

    if (this.isMuted()) {
      this.video.muted = true;
    }
    if (this.isLoop()) {
      this.video.loop = true;
    }
  }

  private setStyle(): void {
    const style = this.style() ?? {};
    if (style.width && style.width === '100%') {
      style.width = `${this.#window.innerWidth}px`;
    }
    if (style.width && style.height === '100%') {
      style.width = `${this.#window.innerHeight}px`;
    }

    if (!this.posterStyle()) {
      this.posterStyle.set({
        ...style,
        width: style.width ? parseFloat(style.width) : undefined,
        height: style.height ? parseFloat(style.height) : undefined,
      });
    }
    for (const property of Object.keys(style) as Array<keyof VideoStyle>) {
      this.video.style.setProperty(property, style[property] || '');
    }
  }

  private observeSize(): void {
    this.videoObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0]!.contentRect;
      if (this.isPlayButtonDisplayed()) {
        if (width <= 1023) {
          this.iconPlaySize.set('4.8rem');
          this.iconPauseSize.set('3.2rem');
          this.iconSoundSize.set('1.7rem');
          this.buttonPauseStyle.set({
            ...this.buttonPauseStyle,
            margin: '0 2.4rem 2.4rem 0',
          });
          this.buttonSoundStyle.set({
            ...this.buttonSoundStyle,
            margin: '0 6.5rem 3.1rem 0',
          });
        } else {
          this.iconPlaySize.set('12.8rem');
          this.iconPauseSize.set('6.4rem');
          this.iconSoundSize.set('3.4rem');
          this.buttonPauseStyle.set({
            ...this.buttonPauseStyle,
            margin: '0 5.6rem 5.6rem 0',
          });
          this.buttonSoundStyle.set({
            ...this.buttonSoundStyle,
            margin: '0 14.9rem 7rem 0',
          });
        }
      }
      const buttonStyle = this.isPlayButtonDisplayed() ? playStyle : pauseStyle;
      this.controlsStyle.set({
        ...this.controlsStyle(),
        marginTop: `-${height / 10}rem`,
        width: `${width / 10}rem`,
        height: `${height / 10}rem`,
        ...buttonStyle,
      });
      this.isVideoDisplayed.set(true);
      setTimeout(() => {
        this.controlsStyle.set({
          ...this.controlsStyle(),
          visibility: 'visible',
        });
      }, 100);
    });
    this.videoObserver.observe(this.video);
  }

  private observeVideoEnd(): void {
    this.video.addEventListener('ended', this.#videoEnded);
  }

  #videoEnded = (): void => {
    this.videoComplete.emit('Video ended');
    this.resetPlayButton();
  };

  /**
   * Handles the video canplaythrough event.
   * Applies autoplay constraints if autoplay is enabled.
   */
  handleCanPlay(): void {
    if (this.autoplay()) {
      this.isMutedInternal.set(true);
      this.isLoopInternal.set(true);
      if (this.video) {
        this.video.muted = true;
        this.video.loop = true;
      }
    }
    this.isCanPlay.set(true);
  }

  /**
   * Handles viewport visibility changes for autoplay coordination.
   * @param inViewport - Whether the video is currently visible in the viewport
   */
  handleViewportChange(inViewport: boolean): void {
    const id = this.videoId();

    if (inViewport) {
      // Restore source if it was lobotomized
      if (this.isLobotomized()) {
        this.isLobotomized.set(false);
      }

      if (this.autoplay()) {
        this.#videoService.addVideoToQueue(id);
      }
    } else {
      // Exiting viewport
      this.isAutoplayError.set(false);
      this.#videoService.removeVideoFromQueue(id);

      // Lobotomize: clear source to release GPU resources
      this.#lobotomize();
    }
  }

  /**
   * Starts autoplay playback.
   */
  #playAutoplay(): void {
    if (!this.#contextService.isBrowser()) return;
    if (!this.video) return;

    this.isPlaying.set(true);
    void this.video.play().catch((error: unknown) => {
      if (error instanceof Error) {
        this.#logsService.debug('VideoComponent', 'Autoplay blocked', error);
        if (error.name === 'NotAllowedError') {
          this.isAutoplayError.set(true);
        }
      }
    });
  }

  /**
   * Stops autoplay playback.
   */
  #stopAutoplay(): void {
    if (this.#contextService.isBrowser() && this.video) {
      this.video.pause();
    }
    this.isPlaying.set(false);
  }

  /**
   * Lobotomizes the video: clears the src and calls load() to release GPU resources.
   * The browser immediately releases hardware resources.
   * The video element stays in the DOM, ready to be reactivated.
   */
  #lobotomize(): void {
    if (!this.#contextService.isBrowser()) return;

    this.video?.pause();
    this.isLobotomized.set(true);
    this.isCanPlay.set(false);
    this.isPlaying.set(false);

    // Force the browser to release GPU resources
    setTimeout(() => {
      if (this.video && this.isLobotomized()) {
        this.video.load();
      }
    }, 0);
  }
}
